// @flow
import * as React from 'react';
import i18n from 'app/common/lib/i18n';
import PincodeCTA from 'app/common/react/components/pincode/PincodeCTA';
import PincodeHeroImagePicker from 'app/common/react/components/pincode/PincodeHeroImagePicker';
import PincodeMainImage from 'app/common/react/components/pincode/PincodeMainImage';
import { track } from 'app/common/lib/react/analytics';
import type { BasicImage, ImageWithDominantColor } from 'app/packages/pinterest-flow-types/image';
import {
  saveCanvasAsPng,
  svgParentToImage,
} from 'app/common/react/components/pincode/downloadUtils';
import { Box, Button, Divider, Flyout, Heading, Spinner, Text } from 'gestalt';

const CONTAINER_WIDTH = 350;
const PINCODE_FULL_PREVIEW_HEIGHT = 288;
const PINCODE_MAIN_PREVIEW_SIZE = 230;
const PINCODE_CTA_PREVIEW_WIDTH = 300;
const PINCODE_CTA_PREVIEW_HEIGHT = 55;

const EXPORT_IMG_SIZE = 3544;

export const MAIN_WRAPPER_CLASSNAME = 'pincodeMain';
export const CTA_WRAPPER_CLASSNAME = 'pincodeCTA';

type Props = {|
  getDownloadImageURL?: (string) => string,
  getMainImageURL?: (string) => string,
  getThumbnailImageSrcSet?: (string) => string,
  id: string,
  images: Array<BasicImage | ImageWithDominantColor>,
  isLoadingImages?: boolean,
  customizable?: boolean,
|};

type State = {|
  ctaIsLoaded: boolean,
  errorFlyoutIsOpen: boolean,
  isDownloading: boolean,
  isFetchingPincodeImage: boolean,
  isPickingHeroImage: boolean,
  pickedImageIndex: number | 'logo',
|};

const getPincodeImageCanvas = (mainImage: Image, ctaImage: Image): window.HTMLCanvasElement => {
  const canvas = document.createElement('canvas');
  canvas.width = EXPORT_IMG_SIZE;
  canvas.height = EXPORT_IMG_SIZE;
  const MAIN_IMAGE_X_PADDING = Math.round(canvas.width * 0.1);
  const CTA_MIN_X_PADDING = Math.round(canvas.width * 0.05);
  const MAIN_IMAGE_SIZE = canvas.width - 2 * MAIN_IMAGE_X_PADDING;
  const CTA_MAX_WIDTH = canvas.width - 2 * CTA_MIN_X_PADDING;
  const CTA_MAX_HEIGHT = canvas.height - MAIN_IMAGE_SIZE;
  const [ctaWidth, ctaHeight] =
    ctaImage.width / ctaImage.height > CTA_MAX_WIDTH / CTA_MAX_HEIGHT
      ? [CTA_MAX_WIDTH, (CTA_MAX_WIDTH / ctaImage.width) * ctaImage.height]
      : [(CTA_MAX_HEIGHT / ctaImage.height) * ctaImage.width, CTA_MAX_HEIGHT];
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(mainImage, MAIN_IMAGE_X_PADDING, 0, MAIN_IMAGE_SIZE, MAIN_IMAGE_SIZE);
  ctx.drawImage(
    ctaImage,
    (canvas.width - ctaWidth) / 2,
    MAIN_IMAGE_SIZE + (canvas.height - MAIN_IMAGE_SIZE - ctaHeight) / 2,
    ctaWidth,
    ctaHeight,
  );
  return canvas;
};

export default class PincodeGenerator extends React.PureComponent<Props, State> {
  state = {
    ctaIsLoaded: false,
    errorFlyoutIsOpen: false,
    isDownloading: false,
    isFetchingPincodeImage: false,
    isPickingHeroImage: false,
    pickedImageIndex: 0,
  };

  getCustomHeroImages = () => {
    const { images, customizable } = this.props;
    const { pickedImageIndex } = this.state;
    if (!customizable) {
      return images;
    }
    const image = pickedImageIndex !== 'logo' && images[pickedImageIndex];
    return image ? [image] : [];
  };

  normalizeUrl = (url: string) => {
    const { getMainImageURL } = this.props;
    const normalizeImageDimension = getMainImageURL || ((url) => url);
    const normalizePinImageDomain = (url: string) =>
      url.replace(/^https:\/\/[^\/]{2,}\.pinimg.com\//, 'https://i.pinimg.com/');
    return normalizePinImageDomain(normalizeImageDimension(url));
  };

  handleFetchChange = (isFetchingPincodeImage: boolean) => {
    this.setState({ isFetchingPincodeImage });
  };

  downloadButton: ?HTMLElement;
  previewNode: ?HTMLElement;

  handleCancelButtonClick = () => this.setState({ isPickingHeroImage: false });
  handleEditButtonClick = () => this.setState({ isPickingHeroImage: true });
  handleSelectImage = (index: number | 'logo') =>
    this.setState({
      isPickingHeroImage: false,
      pickedImageIndex: index,
    });

  downloadPincodeImage = () => {
    const previewNode = this.previewNode;
    const { ctaIsLoaded, isFetchingPincodeImage, isDownloading } = this.state;
    if (!previewNode || !ctaIsLoaded || isFetchingPincodeImage || isDownloading) {
      return;
    }
    const mainElement: ?HTMLElement = previewNode.querySelector(`.${MAIN_WRAPPER_CLASSNAME}`);
    const ctaElement: ?HTMLElement = previewNode.querySelector(`.${CTA_WRAPPER_CLASSNAME}`);
    if (!mainElement || !ctaElement) {
      return;
    }

    const { getDownloadImageURL, id } = this.props;
    track(PCONST.thrift.EventType.PINCODE_CREATE, {
      component: PCONST.thrift.ComponentType.MODAL_PINCODE,
      objectId: id,
    });

    this.setState({ isDownloading: true });
    Promise.all([svgParentToImage(mainElement, getDownloadImageURL), svgParentToImage(ctaElement)])
      .then((values) => {
        const [mainImage, ctaImage] = values;
        return getPincodeImageCanvas(mainImage, ctaImage);
      })
      .then((canvas) => saveCanvasAsPng(canvas, `pincode_${this.props.id}.png`))
      .then(() => {
        this.setState({ isDownloading: false });
      })
      .catch(() => {
        this.setState({ errorFlyoutIsOpen: true });
      });
  };

  render() {
    const { id, isLoadingImages, images, getThumbnailImageSrcSet, customizable } = this.props;
    const {
      ctaIsLoaded,
      errorFlyoutIsOpen,
      isFetchingPincodeImage,
      isDownloading,
      isPickingHeroImage,
    } = this.state;
    if (isPickingHeroImage) {
      return (
        <Box width={CONTAINER_WIDTH} color="white" shape="rounded" padding={4}>
          <Box>
            <Box paddingY={1}>
              <Heading size="xs" truncate>
                {i18n._('Change Image', 'Title of the Pincode hero image picker modal')}
              </Heading>
            </Box>
            <Divider />
            <PincodeHeroImagePicker
              images={images}
              getThumbnailImageSrcSet={getThumbnailImageSrcSet}
              onTouch={this.handleSelectImage}
            />
            <Box padding={1}>
              <Button
                text={i18n._('Cancel', 'Button to dismiss Pincode hero image picker')}
                onClick={this.handleCancelButtonClick}
              />
            </Box>
          </Box>
        </Box>
      );
    }

    return (
      <Box width={CONTAINER_WIDTH} color="white" shape="rounded" padding={4}>
        <Box>
          <div
            ref={(ref) => {
              this.previewNode = ref;
            }}
          >
            <Box
              color="white"
              display="flex"
              direction="column"
              alignItems="center"
              justifyContent="center"
              width="100%"
              height={PINCODE_FULL_PREVIEW_HEIGHT}
            >
              {isLoadingImages ? (
                <Box
                  width="100%"
                  height={PINCODE_MAIN_PREVIEW_SIZE}
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                >
                  <Spinner
                    accessibilityLabel={i18n._(
                      'Loading',
                      'accessibility label for loading spinner',
                    )}
                    show
                  />
                </Box>
              ) : (
                <PincodeMainImage
                  className={MAIN_WRAPPER_CLASSNAME}
                  id={id}
                  imageUrls={this.getCustomHeroImages().map((img) => this.normalizeUrl(img.url))}
                  onFetchingChange={this.handleFetchChange}
                  size={PINCODE_MAIN_PREVIEW_SIZE}
                />
              )}
              <Box display="flex" justifyContent="center" alignItems="center">
                <div className={CTA_WRAPPER_CLASSNAME}>
                  <PincodeCTA
                    onCtaDidLoad={() => this.setState({ ctaIsLoaded: true })}
                    width={PINCODE_CTA_PREVIEW_WIDTH}
                    height={PINCODE_CTA_PREVIEW_HEIGHT}
                  />
                </div>
              </Box>
            </Box>
          </div>
          <Box marginTop={2} padding={1}>
            {customizable &&
              images.length > 0 && (
                <Box paddingY={1}>
                  <Button
                    text={i18n._('Change Image', 'Button to pick Pincode hero image')}
                    onClick={this.handleEditButtonClick}
                  />
                </Box>
              )}
            <Box paddingY={1}>
              <div
                ref={(r) => {
                  this.downloadButton = r;
                }}
              >
                <Button
                  text={i18n._('Download Pincode', 'Button to download the Pincode image')}
                  color="red"
                  onClick={this.downloadPincodeImage}
                  disabled={
                    isLoadingImages || !ctaIsLoaded || isFetchingPincodeImage || isDownloading
                  }
                />
              </div>
              {errorFlyoutIsOpen && (
                <Flyout
                  anchor={this.downloadButton}
                  color="red"
                  idealDirection="down"
                  onDismiss={() => this.setState({ errorFlyoutIsOpen: false })}
                  size="sm"
                  shouldFocus={false}
                >
                  <Box padding={3}>
                    <Text color="white" weight="bold">
                      <span>
                        {i18n._(
                          'Oops! Your browser does not support downloading Pincodes.',
                          'Error message shown when user attempts to download Pincode on a browser not supported',
                        )}
                      </span>
                    </Text>
                  </Box>
                </Flyout>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }
}
